/**
 * Match flow: kickoff, countdown, clock, goals, replay window, overtime.
 *
 * Driven off the fixed 120 Hz tick rather than wall-clock, so a match is fully
 * deterministic and replayable from an input log. Every duration below is
 * therefore stored in seconds but consumed as `dt` accumulations, never as
 * `Date.now()` deltas.
 */

import { DT } from '../physics/constants';

export type MatchPhase =
  /** Pre-kickoff countdown; cars are frozen at their spawn points. */
  | 'countdown'
  /** Normal play; the clock runs. */
  | 'playing'
  /** A goal has been scored; the explosion plays and the clock is stopped. */
  | 'goal'
  /** Goal replay. */
  | 'replay'
  /** Clock has expired but the ball is still live (RL does not end mid-air). */
  | 'overtime-pending'
  /** Golden-goal overtime; the clock counts up. */
  | 'overtime'
  | 'ended';

export interface MatchConfig {
  /** Regulation length in seconds. Standard online match is 5 minutes. */
  duration: number;
  /** Countdown before each kickoff, in seconds. */
  countdownDuration: number;
  /** How long the goal explosion holds before the replay. */
  goalCelebrationDuration: number;
  /** Replay length. */
  replayDuration: number;
  /** When false, a tie at full time simply ends the match. */
  overtimeEnabled: boolean;
}

export const DEFAULT_MATCH_CONFIG: MatchConfig = {
  duration: 300,
  countdownDuration: 3,
  goalCelebrationDuration: 3,
  replayDuration: 6,
  overtimeEnabled: true,
};

export interface GoalEvent {
  /** Team that scored: 0 = blue, 1 = orange. */
  team: 0 | 1;
  /** Index of the car credited with the goal, or null for an own goal. */
  scorer: number | null;
  /** Seconds remaining on the clock when it was scored. */
  clockRemaining: number;
  /** Ball speed at the moment it crossed, for the scoreboard. */
  speed: number;
}

/** The full, plain-data state of a match. See `MatchState.snapshot`. */
export interface MatchSnapshot {
  phase: MatchPhase;
  clock: number;
  score: [number, number];
  phaseTimer: number;
  goals: GoalEvent[];
  inOvertime: boolean;
  nextKickoffTeam: 0 | 1;
  regulationExpired: boolean;
}

export class MatchState {
  phase: MatchPhase = 'countdown';
  /** Seconds remaining in regulation; counts UP during overtime. */
  clock: number;
  score: [number, number] = [0, 0];
  /** Seconds remaining in the current countdown/celebration/replay. */
  phaseTimer: number;
  goals: GoalEvent[] = [];
  /**
   * True once regulation has ended tied and sudden death has begun. Tracked
   * explicitly rather than inferred from the clock: during overtime the clock
   * counts UP from zero, so no clock comparison can distinguish "overtime" from
   * "regulation just expired".
   */
  inOvertime = false;
  /** Which team kicks off next; the conceding team does. */
  private nextKickoffTeam: 0 | 1 = 0;

  constructor(readonly config: MatchConfig = DEFAULT_MATCH_CONFIG) {
    this.clock = config.duration;
    this.phaseTimer = config.countdownDuration;
  }

  /** True while cars and ball must be held frozen at their spawns. */
  get frozen(): boolean {
    return this.phase === 'countdown' || this.phase === 'goal' || this.phase === 'replay';
  }

  /** Countdown number to display (3, 2, 1), or 0 once play starts. */
  get countdownNumber(): number {
    return this.phase === 'countdown' ? Math.ceil(this.phaseTimer) : 0;
  }

  get isTied(): boolean {
    return this.score[0] === this.score[1];
  }

  /**
   * Advance the match by one tick. Returns the phase transition that occurred
   * this tick, if any, so callers can trigger audio/VFX without polling.
   */
  step(dt: number = DT): MatchPhase | null {
    switch (this.phase) {
      case 'countdown':
        this.phaseTimer -= dt;
        if (this.phaseTimer <= 0) {
          this.phaseTimer = 0;
          return this.enter(this.inOvertime ? 'overtime' : 'playing');
        }
        return null;

      case 'playing':
        this.clock -= dt;
        if (this.clock <= 0) {
          this.clock = 0;
          this.regulationExpired = true;
          // Rocket League does not end the half while the ball is live; the
          // caller decides when the ball is "settled" and calls `ballSettled()`.
          return this.enter('overtime-pending');
        }
        return null;

      case 'overtime':
        // Golden goal: the clock counts up and never expires.
        this.clock += dt;
        return null;

      case 'goal':
        this.phaseTimer -= dt;
        if (this.phaseTimer <= 0) {
          this.phaseTimer = this.config.replayDuration;
          return this.enter('replay');
        }
        return null;

      case 'replay':
        this.phaseTimer -= dt;
        if (this.phaseTimer <= 0) return this.finishReplay();
        return null;

      case 'overtime-pending':
      case 'ended':
        return null;
    }
  }

  /**
   * Called when the ball comes to rest or hits the ground after the clock has
   * expired. This is what actually ends regulation.
   */
  ballSettled(): MatchPhase | null {
    if (this.phase !== 'overtime-pending') return null;
    return this.enter(this.beginOvertimeOrEnd());
  }

  /**
   * Regulation has finished. Either start sudden death or end the match.
   * Shared by the clock-expiry path and the post-replay path so the two cannot
   * drift apart.
   */
  private beginOvertimeOrEnd(): MatchPhase {
    if (this.isTied && this.config.overtimeEnabled) {
      this.inOvertime = true;
      this.regulationExpired = false;
      this.clock = 0;
      this.phaseTimer = this.config.countdownDuration;
      return 'countdown';
    }
    return 'ended';
  }

  /** Register a goal. Ignored unless the ball is actually live. */
  scoreGoal(event: GoalEvent): MatchPhase | null {
    if (this.phase !== 'playing' && this.phase !== 'overtime' && this.phase !== 'overtime-pending') {
      return null;
    }
    this.score[event.team]++;
    this.goals.push(event);
    // The conceding team kicks off next.
    this.nextKickoffTeam = event.team === 0 ? 1 : 0;
    this.phaseTimer = this.config.goalCelebrationDuration;
    return this.enter('goal');
  }

  /**
   * Cut the goal celebration or its replay short, at the player's request.
   *
   * Routed through `finishReplay` rather than assigning a phase, because what a
   * replay ends INTO is not always a countdown: a golden goal still ends the
   * match and a goal after the final whistle still resolves regulation. Skipping
   * may change when the replay ends; it must never change what it ends into.
   *
   * Returns the phase entered, or null when there was nothing to skip.
   */
  skipReplay(): MatchPhase | null {
    if (this.phase !== 'goal' && this.phase !== 'replay') return null;
    this.phaseTimer = 0;
    return this.finishReplay();
  }

  /** Team that will kick off after the current stoppage. */
  get kickoffTeam(): 0 | 1 {
    return this.nextKickoffTeam;
  }

  private finishReplay(): MatchPhase {
    // A goal in sudden death is golden: the match ends as soon as it lands.
    if (this.inOvertime) return this.enter('ended');

    // The goal was scored on or after the final whistle, so regulation is over.
    if (this.regulationExpired) return this.enter(this.beginOvertimeOrEnd());

    this.phaseTimer = this.config.countdownDuration;
    return this.enter('countdown');
  }

  /**
   * Set when the clock reaches zero, so that a goal scored in the dying moments
   * still resolves regulation correctly after its replay. Distinct from
   * `clock <= 0`, which is also true at the start of overtime.
   */
  private regulationExpired = false;

  private enter(phase: MatchPhase): MatchPhase {
    this.phase = phase;
    return phase;
  }

  /**
   * Everything the match remembers, as plain data.
   *
   * `nextKickoffTeam` and `regulationExpired` are private and neither is
   * derivable from the public fields — a restored match that lost them would
   * hand the kickoff to the wrong team, and would restart regulation after a
   * dying-seconds goal instead of ending it. That is exactly the kind of
   * silently-wrong restore this pair exists to prevent, so they are included.
   */
  snapshot(): MatchSnapshot {
    return {
      phase: this.phase,
      clock: this.clock,
      score: [this.score[0], this.score[1]],
      phaseTimer: this.phaseTimer,
      goals: this.goals.map((g) => ({ ...g })),
      inOvertime: this.inOvertime,
      nextKickoffTeam: this.nextKickoffTeam,
      regulationExpired: this.regulationExpired,
    };
  }

  /** Inverse of `snapshot`. Mutates in place. */
  restore(s: MatchSnapshot): void {
    this.phase = s.phase;
    this.clock = s.clock;
    this.score = [s.score[0], s.score[1]];
    this.phaseTimer = s.phaseTimer;
    this.goals = s.goals.map((g) => ({ ...g }));
    this.inOvertime = s.inOvertime;
    this.nextKickoffTeam = s.nextKickoffTeam;
    this.regulationExpired = s.regulationExpired;
  }

  /** Clock as mm:ss for the HUD. */
  formatClock(): string {
    const total = Math.max(0, Math.ceil(this.clock));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}

/**
 * Overtime in Rocket League is sudden death with no clock limit. We keep the
 * golden-goal rule but expose this so a UI can show "OVERTIME" rather than a
 * misleading counting-up timer if it prefers.
 */
export const isSuddenDeath = (m: MatchState): boolean => m.inOvertime && m.phase !== 'ended';
